import { Card, Flex, Space, Table, TableColumnsType, Tag, Typography } from "antd";
import { FaxJobStatus, TFaxJobSummary } from "api/services/models/types";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { useLayoutContext } from "components/MainContainer";
import { Sheet } from "react-modal-sheet";

type TFaxJobSummaryGroupByUser = {
  createdByUserId: string;
  faxDate: string;
  numberOfJobs: number;
  ready: number;
  inProgress: number;
  completed: number;
  rejected: number;
};

type TFaxJobSummaryDetail = {
  recipientName: string;
  recipientFaxNumber: string;
  status: string;
  submittedAt: string;
  transmittedAt?: string;
  patientName: string;
  mrn: string;
  provider: string;
  dateOfService: string;
};
const isReady = (summary: TFaxJobSummary) => (summary.status === FaxJobStatus.New ? 1 : 0);
const inProgress = (summary: TFaxJobSummary) =>
  summary.status === FaxJobStatus.Inprogress ||
  summary.status === FaxJobStatus.Spooled ||
  summary.status === FaxJobStatus.Queued ||
  (summary.status === FaxJobStatus.Error && summary.numRetries < 3)
    ? 1
    : 0;
const isCompleted = (summary: TFaxJobSummary) =>
  summary.status === FaxJobStatus.Completed ? 1 : 0;
const isRejected = (summary: TFaxJobSummary) =>
  summary.status === FaxJobStatus.Error && summary.numRetries >= 3 ? 1 : 0;

const groupByUser = (data: TFaxJobSummary[]): TFaxJobSummaryGroupByUser[] => {
  const grouped = data.reduce((op, s) => {
    const createdByUserId = s.createdByUserId;
    op[createdByUserId] = {
      faxDate: s.createdDateTime,
      numberOfJobs: (op[createdByUserId]?.numberOfJobs ?? 0) + 1,
      ready: (op[createdByUserId]?.ready ?? 0) + isReady(s),
      inProgress: (op[createdByUserId]?.inProgress ?? 0) + inProgress(s),
      completed: (op[createdByUserId]?.completed ?? 0) + isCompleted(s),
      rejected: (op[createdByUserId]?.rejected ?? 0) + isRejected(s)
    };
    return op;
  }, {} as Record<string, Partial<TFaxJobSummaryGroupByUser>>);
  return Object.entries(grouped)
    .map(
      ([name, data]) =>
        ({
          createdByUserId: name,
          ...data
        } as TFaxJobSummaryGroupByUser)
    )
    .toSorted((s1, s2) => s1.createdByUserId.localeCompare(s2.createdByUserId));
};

const columns: TableColumnsType<TFaxJobSummaryGroupByUser> = [
  {
    title: "User Id",
    dataIndex: "createdByUserId",
    key: "createdByUserId",
    sorter: (s1, s2) => s1.createdByUserId.localeCompare(s2.createdByUserId)
  },
  {
    title: "Fax Date",
    dataIndex: "faxDate",
    render: d => (d ? dayjs(d).format("MM-DD-YY") : "")
  },
  {
    title: "Total Fax Jobs",
    dataIndex: "numberOfJobs"
  },
  {
    title: "Ready",
    dataIndex: "ready"
  },
  {
    title: "In Progress",
    dataIndex: "inProgress"
  },
  {
    title: "Completed",
    dataIndex: "completed"
  },
  {
    title: "Rejected",
    dataIndex: "rejected"
  }
];

//
const expandedColumns: TableColumnsType<TFaxJobSummaryDetail> = [
  {
    title: "Recipient Name",
    dataIndex: "recipientName",
    key: "recipientName",
    sorter: (s1, s2) => s1.recipientName.localeCompare(s2.recipientName)
  },
  {
    title: "Recipient Fax Number",
    dataIndex: "recipientFaxNumber"
  },
  {
    title: "Status",
    dataIndex: "status",
    render: (status: string) => getTag(status)
  },
  {
    title: "Submitted At",
    dataIndex: "submittedAt",
    render: d => (d ? dayjs(d).format("MM-DD-YY HH:mm:ss") : "")
  },
  {
    title: "Transmitted At",
    dataIndex: "transmittedAt",
    render: d => (d ? dayjs(d).format("MM-DD-YY HH:mm:ss") : "")
  },
  {
    title: "Patient Name",
    dataIndex: "patientName"
  },
  {
    title: "MRN",
    dataIndex: "mrn"
  },
  {
    title: "Provider",
    dataIndex: "provider"
  },
  {
    title: "Date of service",
    dataIndex: "dateOfService",
    render: d => (d ? dayjs(d).format("MM-DD-YY") : "")
  }
];

const getTag = (status: string) => {
  let chipColor = "default";
  let chipBorderColor = "default";
  let chipBGColor = "default";
  let chipText = status ?? "";

  if (
    status === FaxJobStatus.Inprogress ||
    status === FaxJobStatus.Spooled ||
    status === FaxJobStatus.Queued
  ) {
    chipBGColor = "#FFE17633";
    chipBorderColor = "#FFE1764D";
    chipColor = "#cca621";
  }

  if (status === FaxJobStatus.New) {
    chipBGColor = "#76D6FF33";
    chipBorderColor = "#76D6FF4D";
    chipColor = "#76D6FF";
  }

  if (status === FaxJobStatus.Completed) {
    chipBGColor = "#00BE1E33";
    chipBorderColor = "#00BE1E4D";
    chipColor = "#00BE1E";
  }

  if (status === FaxJobStatus.Error) {
    chipBGColor = "#FF979733";
    chipBorderColor = "#FF97974D";
    chipColor = "#FF9797";
  }

  return (
    <Tag
      color={chipBGColor}
      style={{
        borderRadius: ".5rem",
        border: `1px solid ${chipBorderColor}`,
        padding: ".25rem .625rem",
        color: `${chipColor}`
      }}>
      {chipText}
    </Tag>
  );
};

const mapToDetail = (s: TFaxJobSummary): TFaxJobSummaryDetail[] =>
  s.details.map(d => ({
    recipientName: s.recipientName,
    recipientFaxNumber: s.recipientFaxNumber,
    status: s.status,
    submittedAt: s.createdDateTime,
    transmittedAt: s.status === FaxJobStatus.Completed ? s.lastUpdatedDateTime : undefined,
    patientName: d.patientName,
    mrn: d.mrn,
    provider: d.providerName,
    dateOfService: d.visitDate,
    faxJoId: s.faxJobId
  }));

const FaxJobSummaryGrid = ({ data }: { data: TFaxJobSummary[] }) => {
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);
  const { toggleDrawer, isSmallScreen } = useLayoutContext();
  const [open, setOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState<TFaxJobSummaryGroupByUser>();

  useEffect(() => {
    setExpandedRowKeys([]); //reset
  }, [data]);

  useEffect(() => {
    document.body.classList.toggle("no-scroll", !isSmallScreen);
  }, [isSmallScreen]);

  const groupedData = groupByUser(data);
  const expand = (rowKey: string) => setExpandedRowKeys([rowKey]);
  const collapse = (_rowKey: string) => setExpandedRowKeys([]);

  const expandedRowRender = (selectedRow: TFaxJobSummaryGroupByUser) => (
    <Card
      className="card-style"
      title={`Details of User : ${selectedRow.createdByUserId}`}
      styles={{ body: { padding: "0" } }}
      style={{ border: "none", backgroundColor: "#FAFAFA", padding: "1rem", margin: "0" }}>
      <Table<TFaxJobSummaryDetail>
        columns={expandedColumns}
        rowKey={"faxJobId"}
        pagination={false}
        style={{ border: "none" }}
        className="table-style"
        components={{
          body: {
            cell: (props: any) => (
              <td
                {...props}
                style={{
                  borderBottomWidth: "1px",
                  borderBottomColor: "#f0f0f0",
                  borderBottomStyle: "solid",
                  fontWeight: 500
                }}
              />
            )
          }
        }}
        dataSource={data
          .filter(s => selectedRow.createdByUserId === s.createdByUserId)
          .flatMap(s => mapToDetail(s))
          .sort((s1, s2) => new Date(s2.submittedAt).getTime() - new Date(s1.submittedAt).getTime())
        }
      />
    </Card>
  );

  const handleCombinedClick = (item: TFaxJobSummaryGroupByUser | undefined) => {
    setCurrentItem(item);
    setOpen(true);
  };
  interface ProviderCardProps {
    item: TFaxJobSummaryGroupByUser | undefined;
    cardStyle?: React.CSSProperties;
    onClick?: () => void;
  }

  interface ProviderDetailsCardProps {
    item: TFaxJobSummaryDetail | undefined;
    cardStyle?: React.CSSProperties;
    onClick?: () => void;
  }

  const ProviderCard: React.FC<ProviderCardProps> = ({ item, cardStyle = {}, onClick }) => {
    return (
      <Card
        key={item?.createdByUserId}
        style={{ width: "100%", backgroundColor: "#FFF", ...cardStyle }}
        onClick={onClick}>
        <Flex gap="middle" align="end" justify="space-between" style={{ marginBottom: "16px" }}>
          <Typography.Title style={{ fontSize: "1rem" }} className="font-1212">
            {item?.createdByUserId}
          </Typography.Title>
        </Flex>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
            gap: "16px",
            paddingTop: "16px"
          }}>
          {[
            { label: "Fax date", value: dayjs(item?.faxDate).format("MM/DD/YYYY") },
            { label: "Total Fax Jobs", value: item?.numberOfJobs },
            { label: "Ready", value: item?.ready },
            { label: "In progress", value: item?.inProgress },
            { label: "Completed", value: item?.completed },
            { label: "Rejected", value: item?.rejected }
          ].map(({ label, value }) => (
            <div key={label} style={{ display: "flex" }}>
              <span className="font-777" style={{ whiteSpace: "nowrap" }}>
                {label}:{" "}
              </span>
              <span className="font-1212 font-1212-mobile" style={{ whiteSpace: "nowrap" }}>
                {value}
              </span>
            </div>
          ))}
        </div>
      </Card>
    );
  };

  const ProviderDetailsCard: React.FC<ProviderDetailsCardProps> = ({
    item,
    cardStyle = {},
    onClick
  }) => {
    return (
      <Card style={{ width: "100%", backgroundColor: "#FFF", ...cardStyle }} onClick={onClick}>
        <Flex gap="middle" align="end" justify="space-between" style={{ marginBottom: "16px" }}>
          <Typography.Title style={{ fontSize: "1rem" }} className="font-1212">
            {item?.provider}
          </Typography.Title>
          <Space size="small" direction="vertical" style={{ margin: 0 }}>
            {getTag(item?.status ?? "")}
          </Space>
        </Flex>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
            gap: "16px",
            paddingTop: "16px"
          }}>
          {[
            { label: "Recipient fax number", value: item?.recipientFaxNumber },
            { label: "Submitted at", value: dayjs(item?.submittedAt).format("MM/DD/YYYY") },
            { label: "Transmitted at", value: dayjs(item?.transmittedAt).format("MM/DD/YYYY") },
            { label: "Patient name", value: item?.patientName },
            { label: "MRN", value: item?.mrn },
            { label: "Provider", value: item?.provider },
            { label: "Date of service", value: dayjs(item?.dateOfService).format("MM/DD/YYYY") }
          ].map((item, index) => (
            <div
              key={item.label}
              style={{ display: "flex"}}>
              <span className="font-777" style={{ whiteSpace: "nowrap" }}>
                {item.label}:{" "}
              </span>
              <span className="font-1212 font-1212-mobile" style={{ whiteSpace: "nowrap" }}>
                {item.value}
              </span>
            </div>
          ))}
        </div>
      </Card>
    );
  };

  return (
    <>
      {!isSmallScreen ? (
        <Table<TFaxJobSummaryGroupByUser>
          columns={columns}
          dataSource={groupedData}
          pagination={false}
          rowKey={"createdByUserId"}
          className="table-style"
          components={{
            body: {
              cell: (props: any) => (
                <td
                  {...props}
                  style={{
                    borderBottomWidth: "1px",
                    borderBottomColor: "#f0f0f0",
                    borderBottomStyle: "solid",
                    fontWeight: 500
                  }}
                />
              )
            }
          }}
          style={{ border: "none" }}
          expandable={{
            expandedRowRender,
            expandRowByClick: true,
            expandedRowKeys: expandedRowKeys,
            onExpand: (expanded, record) =>
              expanded ? expand(record.createdByUserId) : collapse(record.createdByUserId)
          }}
        />
      ) : (
        <>
          <Flex
            wrap="wrap"
            gap="middle"
            justify="space-between"
            style={{ marginTop: "1.75rem" }}>
            {(() => {
              if (groupedData.length === 0) {
                return (
                  <Typography.Title level={4} style={{ textAlign: "center", width: "100%" }}>
                    No Jobs Found
                  </Typography.Title>
                );
              }

              return groupedData.map(item => (
                <ProviderCard item={item} onClick={() => handleCombinedClick(item)} />
              ));
            })()}
          </Flex>
          <Sheet isOpen={open} onClose={() => setOpen(false)}>
            <Sheet.Container>
              <Sheet.Header />
              <Sheet.Content style={{ padding: "1.25rem" }}>
                <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
                  <ProviderCard
                    item={currentItem}
                    cardStyle={{ backgroundColor: "#3A86FF33" }}
                  />
                  <span
                    style={{ marginTop: "1.25rem", fontSize: "0.875rem" }}
                    className="font-1212">
                    Details Of User id : {currentItem?.createdByUserId}
                  </span>

                  <div
                    style={{
                      flexGrow: 1,
                      overflowY: "auto",
                      marginTop: "1rem"
                    }}>
                    {(() => {
                      if (
                        data.filter(
                          s =>
                            currentItem?.createdByUserId ===
                            (s.createdByUserId == null ? "UNKNOWN" : s.createdByUserId)
                        ).length === 0
                      ) {
                        return (
                          <Typography.Title
                            level={4}
                            style={{ textAlign: "center", width: "100%" }}>
                            No Jobs Found
                          </Typography.Title>
                        );
                      }

                      return data
                        .filter(
                          s =>
                            currentItem?.createdByUserId ===
                            (s.createdByUserId == null ? "UNKNOWN" : s.createdByUserId)
                        )
                        .flatMap(s => mapToDetail(s))
                        .map(item => <ProviderDetailsCard item={item} />);
                    })()}
                  </div>
                </div>
              </Sheet.Content>
            </Sheet.Container>
            <Sheet.Backdrop />
          </Sheet>
        </>
      )}
    </>
  );
};

export default FaxJobSummaryGrid;
